import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, formatApiError } from "../context/AuthContext";
import Seo from "../components/Seo";
import { useLanguage } from "../context/LanguageContext";

export default function Profile() {
  const { t } = useLanguage();
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", iban: "", tax_code: "", address_street: "", address_city: "", address_zip: "", phone: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [busyPwd, setBusyPwd] = useState(false);

  useEffect(() => {
    if (ready && (!user || user === false)) nav("/login");
    if (user && user !== false) {
      setForm({
        name: user.name || "",
        iban: user.iban || "",
        tax_code: user.tax_code || "",
        address_street: user.address_street || "",
        address_city: user.address_city || "",
        address_zip: user.address_zip || "",
        phone: user.phone || "",
      });
    }
  }, [ready, user, nav]);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const updPwd = (k) => (e) => setPwd(p => ({ ...p, [k]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.patch(`${API}/auth/me`, form);
      toast.success(t("Profilo aggiornato"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Errore"));
    } finally { setBusy(false); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 8) { toast.error(t("La nuova password deve avere almeno 8 caratteri")); return; }
    if (pwd.new_password !== pwd.confirm) { toast.error(t("Le password non coincidono")); return; }
    setBusyPwd(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success(t("Password modificata"));
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("Errore"));
    } finally { setBusyPwd(false); }
  };

  if (!user || user === false) return null;

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-12">
      <Seo title="Il mio profilo" path="/profile" noindex />
      <div className="overline text-stone-500">{t("Impostazioni account")}</div>
      <h1 className="font-serif text-5xl tracking-tighter mt-2">{t("Il mio profilo.")}</h1>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <form onSubmit={saveProfile} className="lg:col-span-2 flex flex-col gap-5">
          <div className="font-serif text-2xl">{t("Dati personali e fatturazione")}</div>
          <Field label={t("Nome e cognome")} testId="profile-name" value={form.name} onChange={upd("name")} required />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="IBAN" testId="profile-iban" value={form.iban} onChange={upd("iban")} />
            <Field label={t("Codice Fiscale / P.IVA")} testId="profile-taxcode" value={form.tax_code} onChange={upd("tax_code")} />
          </div>
          <Field label={t("Indirizzo (via e numero)")} testId="profile-street" value={form.address_street} onChange={upd("address_street")} />
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2"><Field label={t("Città")} testId="profile-city" value={form.address_city} onChange={upd("address_city")} /></div>
            <Field label={t("CAP")} testId="profile-zip" value={form.address_zip} onChange={upd("address_zip")} />
          </div>
          <Field label={t("Telefono")} type="tel" testId="profile-phone" value={form.phone} onChange={upd("phone")} />
          <button data-testid="profile-save" disabled={busy} className="mt-4 self-start text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-8 py-4 transition-colors disabled:opacity-50">{busy ? "…" : t("Salva modifiche")}</button>
        </form>

        <div className="lg:col-span-1">
          <form onSubmit={changePwd} className="bg-stone-100 p-8 flex flex-col gap-5">
            <div className="font-serif text-2xl">{t("Cambia password")}</div>
            <Field label={t("Password attuale")} type="password" testId="profile-current-pwd" value={pwd.current_password} onChange={updPwd("current_password")} required />
            <Field label={t("Nuova password (min. 8)")} type="password" testId="profile-new-pwd" value={pwd.new_password} onChange={updPwd("new_password")} required minLength={8} />
            <Field label={t("Conferma nuova password")} type="password" testId="profile-confirm-pwd" value={pwd.confirm} onChange={updPwd("confirm")} required minLength={8} />
            <button data-testid="profile-change-pwd" disabled={busyPwd} className="text-xs uppercase tracking-widest font-semibold bg-[#8C3A3A] hover:bg-[#7F3434] text-stone-50 px-6 py-3 transition-colors disabled:opacity-50">{busyPwd ? "…" : t("Aggiorna password")}</button>
          </form>
          <div className="mt-4 text-xs text-stone-500 leading-relaxed">
            {t("Email account: ")}<span className="font-mono text-stone-900">{user.email}</span>{t(" — non modificabile dal profilo per motivi di sicurezza.")}
          </div>
        </div>
      </div>
    </div>
  );
}
const Field = ({ label, testId, ...props }) => (
  <label className="flex flex-col">
    <span className="overline text-stone-500 mb-2">{label}</span>
    <input data-testid={testId} {...props} className="border-b border-stone-300 bg-transparent py-2 focus:border-stone-900 focus:outline-none" />
  </label>
);
